import React from "react";
import { useQuery } from "@tanstack/react-query";
import { X, Calendar, Clock } from "lucide-react";
import { getClass } from "../../services/classes";
import { ClassSchedule } from "../../types/class";

interface ScheduleManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  classId: string;
}

const days = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
] as const;

const ScheduleManagementModal: React.FC<ScheduleManagementModalProps> = ({
  isOpen,
  onClose,
  classId,
}) => {
  // Fetch class data
  const { data: classResponse, isLoading: classLoading } = useQuery({
    queryKey: ["class", classId],
    queryFn: () => getClass(classId),
    enabled: isOpen && !!classId,
  });

  const classData = classResponse?.class;
  const schedule = classData?.schedule || [];

  if (!isOpen) return null;

  const groupedSchedule = schedule.reduce((acc, period) => {
    const day = period.day;
    if (!acc[day]) {
      acc[day] = [];
    }
    acc[day].push(period);
    return acc;
  }, {} as Record<string, ClassSchedule[]>);

  // Sort periods by start time
  Object.keys(groupedSchedule).forEach((day) => {
    groupedSchedule[day].sort((a, b) => a.startTime.localeCompare(b.startTime));
  });

  return (
    <div className="fixed inset-0 z-[9999] overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 bg-black/50 transition-opacity z-[9998]"
          onClick={onClose}
        ></div>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full relative z-[10000]">
          {/* Header */}
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-purple-100 sm:mx-0 sm:h-10 sm:w-10">
                  <Calendar className="h-6 w-6 text-purple-600" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Class Schedule
                  </h3>
                  <p className="text-sm text-gray-500">
                    {classData?.name} - {classData?.grade}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="bg-white rounded-md text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="bg-white px-4 pb-4 sm:p-6 max-h-[70vh] overflow-y-auto">
            {classLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Schedule Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {days.map((day) => (
                    <div
                      key={day}
                      className="bg-white border border-gray-200 rounded-lg p-4"
                    >
                      <h4 className="text-lg font-medium text-gray-900 mb-3 capitalize">
                        {day}
                      </h4>
                      <div className="space-y-2">
                        {groupedSchedule[day]?.length > 0 ? (
                          groupedSchedule[day].map((period, index) => (
                            <div
                              key={index}
                              className="bg-purple-50 border border-purple-200 rounded-md p-3"
                            >
                              <div className="flex items-center space-x-2 mb-2">
                                <Clock className="h-4 w-4 text-purple-600" />
                                <span className="text-sm font-medium text-gray-900">
                                  {period.startTime} - {period.endTime}
                                </span>
                              </div>
                              <div>
                                <p className="text-sm font-medium text-purple-900">
                                  {period.subjectName}
                                </p>
                                {period.room && (
                                  <p className="text-xs text-purple-700 mt-1">
                                    Room: {period.room}
                                  </p>
                                )}
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="text-center py-6 text-gray-500">
                            <Calendar className="h-8 w-8 mx-auto mb-2 opacity-50" />
                            <p className="text-sm">No periods scheduled</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Summary */}
                {schedule.length > 0 && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-md font-medium text-gray-900 mb-2">
                      Schedule Summary
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="font-medium text-gray-700">
                          Total Periods:
                        </span>
                        <span className="ml-2 text-gray-900">
                          {schedule.length}
                        </span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">
                          Unique Subjects:
                        </span>
                        <span className="ml-2 text-gray-900">
                          {new Set(schedule.map((p) => p.subjectName)).size}
                        </span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">
                          Active Days:
                        </span>
                        <span className="ml-2 text-gray-900">
                          {Object.keys(groupedSchedule).length}
                        </span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">
                          With Rooms:
                        </span>
                        <span className="ml-2 text-gray-900">
                          {schedule.filter((p) => p.room).length}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              onClick={onClose}
              className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScheduleManagementModal;
